// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import iconsObject from "@rugby/XDL/Icons/rugbyCommonIcons";
import * as React from "react";
import { useState } from "react";
import styled, { css, withTheme } from "styled-components";

export type NotifyProps = MobiusStyledComponentProps<
    "div",
    {
        /** Text to display as the title of the notify. Will be ignored if `children` are passed. */
        title?: React.ReactNode;
        /** Text to display in the body of the notify. Will be ignored if `children` are passed. */
        body?: React.ReactNode;
        /** The type of message to be displayed, which governs color and iconography. */
        messageType?: "success" | "danger" | "info" | "warning";
        /** Description of type of message to be read to screenreader, to replace the iconography and color in relaying
         * the importance or valence of the message. Defaults to `messageType` value if not passed in.
         * Can be used for translations or additional specificity if necessary. */
        messageTypeString?: string;
        notifyContainerOverrides?: StyledProp;
        iconContainerOverrides?: StyledProp;
        notifyTitleOverrides?: StyledProp;
        notifyBodyOverrides?: StyledProp;
        showCloseButton?: boolean;
    }
>;

const NotifyContainer = styled.div<any>`
    display: flex;
    width: 100%;
    max-width: 560px;
    border-radius: 3px;
    background-color: ${props => safeColor(resolveColor("text.main", props.theme)).rgb().alpha(0.9).string()};
    ${injectCss}
`;

const IconContainer = styled.div<any>`
    display: flex;
    align-items: center;
    background-color: ${props => safeColor(resolveColor("text.main", props.theme)).toString()};
    border-radius: 3px 0 0 3px;

    ${injectCss};
`;
const NotifyTitle = styled.div<InjectableCss>`
    margin-right: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: bold;
    ${injectCss}
`;

const NotifyBody = styled.div<InjectableCss>`
    margin-right: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

const CloseButtonContainer = styled.div<InjectableCss>`
    margin-right: 12px;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    cursor: pointer;
    ${injectCss}
`;

const Notify: React.FC<NotifyProps> = ({
    messageType,
    messageTypeString,
    children,
    title,
    body,
    theme,
    notifyContainerOverrides,
    iconContainerOverrides,
    notifyTitleOverrides,
    notifyBodyOverrides,
    showCloseButton,
    ...otherProps
}: NotifyProps) => {
    const iconSrcByMessageMap = {
        success: "CheckmarkRound",
        danger: "CloseRound",
        warning: "Attention",
        info: "Info",
    };
    const [visible, setVisibility] = useState(true);

    if (visible) {
        return (
            <NotifyContainer
                css={notifyContainerOverrides}
                messageType={messageType}
                data-test-selector={`notify${messageType}`}
                role="status"
                aria-live="polite"
                {...otherProps}
            >
                {children || (
                    <>
                        <IconContainer css={iconContainerOverrides}>
                            <Icon
                                src={
                                    messageType &&
                                    iconSrcByMessageMap &&
                                    iconSrcByMessageMap[messageType] &&
                                    iconsObject[iconSrcByMessageMap[messageType]]
                                }
                                color={messageType}
                                size={20}
                                css={css`
                                    margin: 9px 11px;
                                `}
                            />
                        </IconContainer>
                        <StyledWrapper
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                margin: "12px",
                                flexGrow: 1,
                            }}
                        >
                            {title && (
                                <NotifyTitle css={notifyTitleOverrides}>
                                    <Typography
                                        color={getColor("common.white")({ theme })}
                                        data-test-selector="notifyTitle"
                                    >
                                        {title}:
                                    </Typography>
                                </NotifyTitle>
                            )}
                            <NotifyBody css={notifyBodyOverrides} data-id="notify-body">
                                <VisuallyHidden>{messageTypeString || messageType}</VisuallyHidden>

                                <Typography color={getColor("common.white")({ theme })} data-test-selector="notifyBody">
                                    {body}
                                </Typography>
                            </NotifyBody>
                        </StyledWrapper>
                        {showCloseButton && (
                            <CloseButtonContainer onClick={() => setVisibility(!visible)}>
                                <Icon
                                    src={iconsObject["Close"]}
                                    size={10}
                                    css={css`
                                        margin: 15px;
                                        color: ${getColor("common.white")({ theme })};
                                    `}
                                />
                            </CloseButtonContainer>
                        )}
                    </>
                )}
            </NotifyContainer>
        );
    }
    return <></>;
};

/** @component */
export default withTheme(Notify);
